import { useMutation, useSuspenseQuery } from "@apollo/client";
import { Button, PrimaryFullScreenSpinner, toast, useAppForm } from "@repo/ui";
import { decimalSchema, knownProductIds } from "@repo/utils";
import { useStore } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Decimal from "decimal.js";
import { Suspense } from "react";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/fund-distributions_/new")({
  head: () => opsHead("New Fund Distribution"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const formSchema = z.object({
  custodyAccountId: z.string().nonempty({ message: "Required" }),
  payDate: z.date({ error: "Required" }),
  unitPrice: decimalSchema.refine((v) => v.gt(0), "Must be greater than 0"),
  totalUnitAmount: decimalSchema.refine(
    (v) => v.gt(0),
    "Must be greater than 0",
  ),
});

type FormData = z.input<typeof formSchema>;

function View() {
  const navigate = useNavigate();

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminNewFundDistributionForm {
        custodyAccounts {
          id
          shortRef
          currency
          product {
            __typename
            id
            internalName
          }
        }
      }
    `),
  );

  const [createFundDistribution, { loading }] = useMutation(
    graphql(`
      mutation AdminNewFundDistribution_Create(
        $input: CreateFundDistributionInput!
      ) {
        createFundDistribution(input: $input) {
          fundDistribution {
            id
          }
        }
      }
    `),
  );

  const custodyAccountOptions = (data.custodyAccounts ?? [])
    .filter((c) => c.product?.__typename === "FundProduct")
    .map((c) => ({
      value: c.id,
      label: `${c.product?.internalName ?? "—"} · ${c.shortRef} · ${c.currency}`,
      currency: c.currency,
      productId: c.product?.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const form = useAppForm({
    defaultValues: {
      custodyAccountId: "",
    } as unknown as FormData,
    validators: { onSubmit: formSchema },
    onSubmit: async ({ value }) => {
      const parsed = formSchema.parse(value);
      const custodyAccount = custodyAccountOptions.find(
        (c) => c.value === parsed.custodyAccountId,
      );
      if (!custodyAccount) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Select a custody account",
        });
        return;
      }

      await createFundDistribution({
        variables: {
          input: {
            custodyAccountId: parsed.custodyAccountId,
            payDate: parsed.payDate,
            unitPriceCurrency: custodyAccount.currency,
            unitPrice: parsed.unitPrice,
            totalUnitAmount: parsed.totalUnitAmount,
          },
        },
        onCompleted: (result) => {
          const id = result.createFundDistribution?.fundDistribution.id;
          if (!id) {
            toast({
              kind: "error",
              autoHide: false,
              title: "Failed to create fund distribution",
            });
            return;
          }
          toast({
            kind: "success",
            autoHide: true,
            title: "Created fund distribution",
          });
          void navigate({
            to: "/fund-distributions/$fundDistributionId",
            params: { fundDistributionId: id },
          });
        },
        onError: (error) => {
          toast({
            kind: "error",
            autoHide: false,
            title: "Failed to create fund distribution",
            description:
              error.graphQLErrors?.[0]?.message ?? "Please try again later.",
          });
        },
      });
    },
  });

  const selectedCustodyAccountId = useStore(
    form.store,
    (state) => state.values.custodyAccountId,
  );
  const selectedCustodyAccount = custodyAccountOptions.find(
    (c) => c.value === selectedCustodyAccountId,
  );

  return (
    <AdminPageContainer testId="admin-fund-distributions-new">
      <AdminPageHeader
        title="New Fund Distribution"
        subtitle="Creates, allocates and reconciles the distribution, posting the reinvestment to the ledger."
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="flex max-w-xl flex-col gap-8">
          <form.AppField
            name="custodyAccountId"
            listeners={{
              onChange: ({ value }) => {
                const account = custodyAccountOptions.find(
                  (c) => c.value === value,
                );
                if (
                  account?.productId ===
                  knownProductIds.stateStreetAustralianCashTrust
                ) {
                  form.setFieldValue("unitPrice", new Decimal("1.0"));
                }
              },
            }}
          >
            {(field) => (
              <field.SelectField
                label="Custody account"
                placeholder="Select..."
              >
                {custodyAccountOptions.map((o) => (
                  <field.SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </field.SelectItem>
                ))}
              </field.SelectField>
            )}
          </form.AppField>

          <form.AppField name="payDate">
            {(field) => (
              <field.AnnotatedField label="Pay date">
                <field.DatePickerField placeholder="Pick a date" />
              </field.AnnotatedField>
            )}
          </form.AppField>

          <form.AppField name="unitPrice">
            {(field) => (
              <field.CurrencyAmountField
                currency={selectedCustodyAccount?.currency ?? "AUD"}
                align="start"
                label="Unit price"
                placeholder="Unit price"
              />
            )}
          </form.AppField>

          <form.AppField name="totalUnitAmount">
            {(field) => (
              <field.UnitAmountField
                label="Total units"
                placeholder="Total units"
                decimalScale={5}
              />
            )}
          </form.AppField>

          <div className="flex justify-end">
            <Button variant="primary" type="submit" disabled={loading}>
              Create fund distribution
            </Button>
          </div>
        </div>
      </form>
    </AdminPageContainer>
  );
}
